import React, { useState, useRef, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import { format } from "date-fns";
import openSocket from "../../services/socket-io";
import useSound from "use-sound";
import { toast } from "react-toastify";

import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import Popover from "@material-ui/core/Popover";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import ListItemText from "@material-ui/core/ListItemText";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import Badge from "@material-ui/core/Badge";
import NotificationsIcon from "@material-ui/icons/Notifications";

import TicketListItem from "../TicketListItem";
import { i18n } from "../../translate/i18n";
import useTickets from "../../hooks/useTickets";
import alertSound from "../../assets/sound.mp3";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { registerWebPushSubscription } from "../../services/push";

const useStyles = makeStyles(theme => ({
	tabContainer: {
		overflowY: "auto",
		maxHeight: 360,
		...theme.scrollbarStyles,
	},
	popoverPaper: {
		width: "100%",
		maxWidth: 380,
		marginLeft: theme.spacing(2),
		marginRight: theme.spacing(1),
		[theme.breakpoints.down("sm")]: {
			maxWidth: 300,
		},
	},
	noShadow: {
		boxShadow: "none !important",
	},
	iconButton: {
		color: theme.palette.text.primary,
	},
	header: {
		borderBottom: `1px solid ${theme.palette.divider}`,
	},
	internalItem: {
		alignItems: "flex-start",
	},
	internalPrimary: {
		fontWeight: 500,
	},
	internalSecondary: {
		display: "block",
		marginTop: theme.spacing(0.5),
	},
	footer: {
		display: "flex",
		justifyContent: "flex-end",
		padding: theme.spacing(1),
		borderTop: `1px solid ${theme.palette.divider}`,
	},
	emptyState: {
		padding: theme.spacing(2),
	},
}));

const getNotificationTag = notificationId => `notification:${notificationId}`;
const REFRESH_INTERVAL_MS = 8000;
const SOCKET_NOTIFICATION_SUPPRESSION_WINDOW_MS = 6000;
const PUSH_SYNC_INTERVAL_MS = 10 * 60 * 1000;

const NotificationsPopOver = () => {
	const classes = useStyles();

	const history = useHistory();
	const { user } = useContext(AuthContext);
	const ticketIdUrl = +history.location.pathname.split("/")[2];
	const ticketIdRef = useRef(ticketIdUrl);
	const anchorEl = useRef();
	const [isOpen, setIsOpen] = useState(false);
	const [notifications, setNotifications] = useState([]);
	const [internalNotifications, setInternalNotifications] = useState([]);
	const [activeTab, setActiveTab] = useState(0);
	const [refreshTrigger, setRefreshTrigger] = useState(0);

	const [, setDesktopNotifications] = useState([]);
	const unreadSnapshotRef = useRef(new Map());
	const hasUnreadSnapshotRef = useRef(false);
	const lastTicketNotificationAtRef = useRef(new Map());
	const lastPushSyncAtRef = useRef(0);

	const { tickets } = useTickets({
		withUnreadMessages: "true",
		refreshTrigger,
	});
	const [play] = useSound(alertSound);
	const soundAlertRef = useRef();

	const historyRef = useRef(history);

	useEffect(() => {
		soundAlertRef.current = play;

		if (!("Notification" in window)) {
			console.log("This browser doesn't support notifications");
		}
	}, [play]);

	useEffect(() => {
		if (!("Notification" in window) || Notification.permission !== "default") {
			return undefined;
		}

		requestNotificationPermissionIfNeeded().catch(() => undefined);
		return undefined;
	}, []);

	useEffect(() => {
		if (!("Notification" in window) || Notification.permission !== "default") {
			return undefined;
		}

		let handled = false;

		const requestByGesture = async () => {
			if (handled) {
				return;
			}

			handled = true;
			await requestNotificationPermissionIfNeeded();
			window.removeEventListener("pointerdown", requestByGesture);
			window.removeEventListener("keydown", requestByGesture);
		};

		window.addEventListener("pointerdown", requestByGesture, { passive: true });
		window.addEventListener("keydown", requestByGesture);

		return () => {
			window.removeEventListener("pointerdown", requestByGesture);
			window.removeEventListener("keydown", requestByGesture);
		};
	}, []);

	const syncWebPushSubscriptionIfNeeded = async (force = false) => {
		if (!user?.id) {
			return;
		}

		const now = Date.now();
		if (!force && now - lastPushSyncAtRef.current < PUSH_SYNC_INTERVAL_MS) {
			return;
		}

		try {
			await registerWebPushSubscription();
			lastPushSyncAtRef.current = now;
		} catch (error) {
			// ignore sync errors and keep regular in-app notifications
		}
	};

	useEffect(() => {
		if (!user?.id) {
			return undefined;
		}

		if (!("Notification" in window) || Notification.permission !== "granted") {
			return undefined;
		}

		syncWebPushSubscriptionIfNeeded(true).catch(() => undefined);
		return undefined;
	}, [user?.id]);

	const markTicketAsNotified = ticketId => {
		if (!ticketId) {
			return;
		}

		const now = Date.now();
		lastTicketNotificationAtRef.current.set(ticketId, now);

		if (lastTicketNotificationAtRef.current.size > 200) {
			const cutoff = now - 15 * 60 * 1000;
			lastTicketNotificationAtRef.current.forEach((timestamp, id) => {
				if (timestamp < cutoff) {
					lastTicketNotificationAtRef.current.delete(id);
				}
			});
		}
	};

	const shouldSuppressTicketNotification = ticketId => {
		const lastNotifiedAt = Number(
			lastTicketNotificationAtRef.current.get(ticketId) || 0
		);
		return Date.now() - lastNotifiedAt < SOCKET_NOTIFICATION_SUPPRESSION_WINDOW_MS;
	};

	useEffect(() => {
		setNotifications(tickets);

		const currentUnreadByTicket = new Map();
		tickets.forEach(ticket => {
			currentUnreadByTicket.set(ticket.id, Number(ticket?.unreadMessages || 0));
		});

		if (!hasUnreadSnapshotRef.current) {
			hasUnreadSnapshotRef.current = true;
			unreadSnapshotRef.current = currentUnreadByTicket;
			return;
		}

		tickets.forEach(ticket => {
			const currentUnread = Number(ticket?.unreadMessages || 0);
			const previousUnread = Number(
				unreadSnapshotRef.current.get(ticket.id) || 0
			);

			if (
				currentUnread > previousUnread &&
				!shouldSuppressTicketNotification(ticket.id)
			) {
				const shouldNotNotificate =
					(ticket.id === ticketIdRef.current &&
						document.visibilityState === "visible") ||
					ticket.isGroup;

				if (shouldNotNotificate) {
					return;
				}

				const fallbackBody =
					String(ticket.lastMessage || "").trim() || "Nova mensagem";
				const options = {
					body: `${fallbackBody} - ${format(new Date(), "HH:mm")}`,
					icon: ticket.contact?.profilePicUrl || undefined,
					tag: String(ticket.id),
					renotify: true,
				};

				createDesktopNotification({
					title: `${i18n.t("tickets.notification.message")} ${
						ticket.contact?.name || "Contato"
					}`,
					options,
					targetPath: `/tickets/${ticket.id}`,
					onClick: () => historyRef.current.push(`/tickets/${ticket.id}`),
				})
					.then(() => {
						markTicketAsNotified(ticket.id);
						if (soundAlertRef.current) {
							soundAlertRef.current();
						}
					})
					.catch(() => undefined);
			}
		});

		unreadSnapshotRef.current = currentUnreadByTicket;
	}, [tickets]);

	useEffect(() => {
		const fetchInternalNotifications = async () => {
			try {
				const { data } = await api.get("/notifications", {
					params: { unreadOnly: true }
				});
				setInternalNotifications(data);
			} catch (err) {
				toastError(err);
			}
		};

		if (user?.id) {
			fetchInternalNotifications();
		}
	}, [user, refreshTrigger]);

	useEffect(() => {
		if (!user?.id) {
			return undefined;
		}

		const triggerRefresh = () => {
			setRefreshTrigger(prevState => prevState + 1);
		};

		const handleVisibilityChange = () => {
			if (!document.hidden) {
				triggerRefresh();
			}
		};

		const handleFocus = () => {
			triggerRefresh();
		};

		const handleOnline = () => {
			triggerRefresh();
		};

		const refreshInterval = setInterval(() => {
			triggerRefresh();
		}, REFRESH_INTERVAL_MS);

		document.addEventListener("visibilitychange", handleVisibilityChange);
		window.addEventListener("focus", handleFocus);
		window.addEventListener("online", handleOnline);

		return () => {
			clearInterval(refreshInterval);
			document.removeEventListener("visibilitychange", handleVisibilityChange);
			window.removeEventListener("focus", handleFocus);
			window.removeEventListener("online", handleOnline);
		};
	}, [user]);

	useEffect(() => {
		ticketIdRef.current = ticketIdUrl;
	}, [ticketIdUrl]);

	useEffect(() => {
		if (!user?.id) {
			return undefined;
		}

		const socket = openSocket();
		const joinNotificationRoom = () => {
			socket.emit("joinNotification");
			setRefreshTrigger(prevState => prevState + 1);
		};

		socket.on("connect", joinNotificationRoom);
		joinNotificationRoom();

		socket.on("ticket", data => {
			if (data.action === "updateUnread" || data.action === "delete") {
				setNotifications(prevState => {
					const ticketIndex = prevState.findIndex(t => t.id === data.ticketId);
					if (ticketIndex !== -1) {
						return prevState.filter(ticket => ticket.id !== data.ticketId);
					}
					return prevState;
				});

				setDesktopNotifications(prevState => {
					const notfiticationIndex = prevState.findIndex(
						n => n.tag === String(data.ticketId)
					);
					if (notfiticationIndex !== -1) {
						prevState[notfiticationIndex].close();
						return prevState.filter(
							notification => notification.tag !== String(data.ticketId)
						);
					}
					return prevState;
				});
			}
		});

		socket.on("appMessage", data => {
			if (
				data.action === "create" &&
				!data.message.fromMe
			) {
				setNotifications(prevState => {
					const ticketIndex = prevState.findIndex(t => t.id === data.ticket.id);
					if (ticketIndex !== -1) {
						return prevState.map(ticket =>
							ticket.id === data.ticket.id ? data.ticket : ticket
						);
					}
					return [data.ticket, ...prevState];
				});

				const shouldNotNotificate =
					(data.message.ticketId === ticketIdRef.current &&
						document.visibilityState === "visible") ||
					data.ticket.isGroup;

				if (shouldNotNotificate) return;

				handleNotifications(data).catch(() => undefined);
			}
		});

		socket.on("userNotification", data => {
			if (data.action === "readAll") {
				setInternalNotifications([]);
				setDesktopNotifications(prevState => {
					prevState
						.filter(notification =>
							String(notification.tag).startsWith("notification:")
						)
						.forEach(notification => notification.close());

					return prevState.filter(
						notification =>
							!String(notification.tag).startsWith("notification:")
					);
				});
				return;
			}

			if (data.notification) {
				setInternalNotifications(prevState => {
					const nextState = prevState.filter(
						item => item.id !== data.notification.id
					);

					if (!data.notification.readAt) {
						return [data.notification, ...nextState];
					}

					return nextState;
				});
			}

			if (data.action === "create" && data.notification && !data.notification.readAt) {
				handleInternalNotification(data.notification).catch(() => undefined);
			}

			if (data.action === "update" && data.notification?.readAt) {
				setDesktopNotifications(prevState => {
					const notificationTag = getNotificationTag(data.notification.id);
					const desktopNotification = prevState.find(
						item => item.tag === notificationTag
					);

					if (desktopNotification) {
						desktopNotification.close();
					}

					return prevState.filter(item => item.tag !== notificationTag);
				});
			}
		});

		return () => {
			socket.disconnect();
		};
	}, [user]);

	const requestNotificationPermissionIfNeeded = async () => {
		if (!("Notification" in window)) {
			return "unsupported";
		}

		if (Notification.permission !== "default") {
			return Notification.permission;
		}

		try {
			const permission = await Notification.requestPermission();
			if (permission === "granted") {
				await syncWebPushSubscriptionIfNeeded(true);
			}

			return permission;
		} catch (error) {
			return Notification.permission;
		}
	};

	const createDesktopNotification = async ({
		title,
		options,
		onClick,
		targetPath,
	}) => {
		const permission = await requestNotificationPermissionIfNeeded();

		if (permission !== "granted") {
			return null;
		}

		const useServiceWorkerNotification =
			document.visibilityState !== "visible" &&
			"serviceWorker" in navigator &&
			navigator.serviceWorker;

		if (useServiceWorkerNotification) {
			try {
				const registration =
					(await navigator.serviceWorker.getRegistration()) ||
					(await navigator.serviceWorker.ready);
				if (registration) {
					await registration.showNotification(title, {
						...options,
						data: {
							...(options?.data || {}),
							url: targetPath,
						},
					});
					return null;
				}
			} catch (error) {
				// fallback to standard Notification below
			}
		}

		const notification = new Notification(title, options);
		notification.onclick = event => {
			event.preventDefault();
			window.focus();
			if (typeof onClick === "function") {
				onClick();
			}
		};

		return notification;
	};

	const handleNotifications = async data => {
		const { message, contact, ticket } = data;
		markTicketAsNotified(ticket?.id);
		const body = `${message.body || ticket?.lastMessage || "Nova mensagem"}`.trim();

		const options = {
			body: `${body || "Nova mensagem"} - ${format(new Date(), "HH:mm")}`,
			icon: contact.profilePicUrl || ticket?.contact?.profilePicUrl || undefined,
			tag: ticket.id,
			renotify: true,
		};

		const notification = await createDesktopNotification({
			title: `${i18n.t("tickets.notification.message")} ${contact.name}`,
			options,
			targetPath: `/tickets/${ticket.id}`,
			onClick: () => historyRef.current.push(`/tickets/${ticket.id}`),
		});

		if (notification) {
			setDesktopNotifications(prevState => {
				const notfiticationIndex = prevState.findIndex(
					n => n.tag === notification.tag
				);
				if (notfiticationIndex !== -1) {
					return prevState.map(item =>
						item.tag === notification.tag ? notification : item
					);
				}
				return [notification, ...prevState];
			});
		}

		soundAlertRef.current();
	};

	const handleInternalNotification = async notification => {
		const shouldSkip =
			document.visibilityState === "visible" && history.location.pathname === "/tasks";

		if (shouldSkip) {
			return;
		}

		const options = {
			body: `${notification.description || "Nova notificacao interna"} - ${format(
				new Date(),
				"HH:mm"
			)}`,
			icon: notification.createdByUser?.profileImageUrl || undefined,
			tag: getNotificationTag(notification.id),
			renotify: true,
		};

		const desktopNotification = await createDesktopNotification({
			title: notification.title,
			options,
			targetPath: "/tasks",
			onClick: () => {
				handleOpenInternalNotification(notification).catch(() => undefined);
			},
		});

		if (desktopNotification) {
			setDesktopNotifications(prevState => {
				const nextState = prevState.filter(
					item => item.tag !== desktopNotification.tag
				);
				return [desktopNotification, ...nextState];
			});
		}

		soundAlertRef.current();
	};

	const handleClick = () => {
		requestNotificationPermissionIfNeeded()
			.then(permission => {
				if (permission === "denied") {
					toast.warn("Notificacoes bloqueadas no navegador. Permita nas configuracoes do site.");
				}
			})
			.catch(() => undefined);
		setIsOpen(prevState => !prevState);
	};

	const handleClickAway = () => {
		setIsOpen(false);
	};

	const NotificationTicket = ({ children }) => {
		return <div onClick={handleClickAway}>{children}</div>;
	};

	const handleOpenInternalNotification = async notification => {
		try {
			await api.put(`/notifications/${notification.id}/read`);
		} catch (err) {
			toastError(err);
		} finally {
			historyRef.current.push("/tasks");
			handleClickAway();
		}
	};

	const handleReadAll = async () => {
		try {
			await api.put("/notifications/read-all");
		} catch (err) {
			toastError(err);
		}
	};

	const totalNotifications = notifications.length + internalNotifications.length;

	return (
		<>
			<IconButton
				onClick={handleClick}
				ref={anchorEl}
				aria-label="Open Notifications"
				className={classes.iconButton}
			>
				<Badge badgeContent={totalNotifications} color="secondary">
					<NotificationsIcon />
				</Badge>
			</IconButton>
			<Popover
				disableScrollLock
				open={isOpen}
				anchorEl={anchorEl.current}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				classes={{ paper: classes.popoverPaper }}
				onClose={handleClickAway}
			>
				<div className={classes.header}>
					<Tabs
						value={activeTab}
						onChange={(event, value) => setActiveTab(value)}
						variant="fullWidth"
						indicatorColor="primary"
						textColor="primary"
					>
						<Tab label={`Conversas (${notifications.length})`} />
						<Tab label={`Internas (${internalNotifications.length})`} />
					</Tabs>
				</div>

				{activeTab === 0 && (
					<List dense className={classes.tabContainer}>
						{notifications.length === 0 ? (
							<ListItem>
								<ListItemText>{i18n.t("notifications.noTickets")}</ListItemText>
							</ListItem>
						) : (
							notifications.map(ticket => (
								<NotificationTicket key={ticket.id}>
									<TicketListItem ticket={ticket} />
								</NotificationTicket>
							))
						)}
					</List>
				)}

				{activeTab === 1 && (
					<>
						<List dense className={classes.tabContainer}>
							{internalNotifications.length === 0 ? (
								<div className={classes.emptyState}>
									<Typography variant="body2" color="textSecondary">
										Nenhuma notificacao interna pendente.
									</Typography>
								</div>
							) : (
								internalNotifications.map(notification => (
									<ListItem
										button
										key={notification.id}
										onClick={() => handleOpenInternalNotification(notification)}
										className={classes.internalItem}
									>
										<ListItemAvatar>
											<Avatar src={notification.createdByUser?.profileImageUrl}>
												{(
													notification.createdByUser?.firstName ||
													notification.createdByUser?.name ||
													"N"
												).charAt(0)}
											</Avatar>
										</ListItemAvatar>
										<ListItemText
											primary={
												<span className={classes.internalPrimary}>
													{notification.title}
												</span>
											}
											secondary={
												<>
													<span className={classes.internalSecondary}>
														{notification.description || "Sem descricao"}
													</span>
													<span className={classes.internalSecondary}>
														{notification.createdByUser?.name
															? `Por ${notification.createdByUser.name}`
															: "Notificacao interna"}
													</span>
												</>
											}
										/>
									</ListItem>
								))
							)}
						</List>
						{internalNotifications.length > 0 && (
							<div className={classes.footer}>
								<Button color="primary" size="small" onClick={handleReadAll}>
									Marcar todas como lidas
								</Button>
							</div>
						)}
					</>
				)}
			</Popover>
		</>
	);
};

export default NotificationsPopOver;
